import {ChangeDetectionStrategy, Component, effect, inject, input, model, OnDestroy, OnInit} from '@angular/core';
import {AngularEditorConfig, AngularEditorModule, UploadResponse} from '@kolkov/angular-editor';
import {from, Observable, Subscription, throwError} from 'rxjs';
import {HttpEvent} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {ReactiveFormsModule, UntypedFormBuilder} from '@angular/forms';

@Component({
  selector: 'xui-basic-editor',
  templateUrl: './basic-editor.component.html',
  styleUrl: './basic-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AngularEditorModule, ReactiveFormsModule]
})
export class BasicEditorComponent implements OnInit, OnDestroy {

  private formBuilder = inject(UntypedFormBuilder);

  static maxImageSize = 350 * 1024;
  readonly content = model('');
  readonly debug = input(false);
  config: AngularEditorConfig = {
    editable: true,
    minHeight: '10em',
    minWidth: '0',
    enableToolbar: true,
    showToolbar: true,
    toolbarPosition: 'top',
    upload: this.convertFileToBase64.bind(this),
    toolbarHiddenButtons: [
      ['italic', 'underline', 'strikeThrough', 'undo', 'redo', 'subscript', 'superscript', 'justifyLeft', 'justifyCenter', 'justifyRight', 'indent', 'outdent'],
      ['backgroundColor', 'customClasses', 'insertVideo', 'insertHorizontalRule', 'toggleEditorMode']]
  };

  private subscription: Subscription;
  contentInput = this.formBuilder.group({
    content: ['']
  });

  constructor() {
    effect(() => {
      const content = this.content();
      if (this.contentInput.value.content !== content) {
        this.contentInput.setValue({content}, {emitEvent: false});
      }
    });
  }

  get value(): string {
    return this.contentInput.value.content;
  }

  ngOnInit(): void {
    this.contentInput.setValue({content: this.content()});
    this.subscription = this.contentInput.valueChanges.subscribe(value => {
      this.content.set(value.content ?? '');
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private convertFileToBase64(file: File): Observable<HttpEvent<UploadResponse>> {
    if (file.size > BasicEditorComponent.maxImageSize) {
      return throwError(new Error('File size exceeds 350KB'));
    }
    return from(this.convertToBase64(file)).pipe(map(base64 => {
      const response: UploadResponse = {imageUrl: base64};
      return {type: 0, body: response} as HttpEvent<UploadResponse>;
    }));
  }

  private convertToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  }
}
